import { useEffect } from 'react';

/**
 * SEOHead — sets <title>, meta description, canonical URL, and Open Graph tags
 * for the current page. Call this from the top of every page component.
 *
 * Why a custom hook instead of react-helmet?
 *   - Zero new dependencies (we already ship lots of JS)
 *   - Direct DOM API is plenty fast and lets us also clean up on unmount
 *   - Plays nice with the bot-aware OG bootstrap script in index.html
 *
 * Safe to call with partial props — falls back to JS Fragrance brand defaults.
 */
const SITE_NAME = 'JS Fragrance';
const SITE_URL = 'https://jsfragrances.com';
const DEFAULT_DESC =
  'EDP bottled at 30% oil concentration — Extrait-level performance from JS Fragrance. Long-lasting luxury fragrances na affordable. Hand-blended in the Philippines.';
const DEFAULT_IMG = `${SITE_URL}/api/files/jsfragrance/share-images/caf72789-0f07-4f6e-8ad8-f6522c3c62a1.png`;

const upsertMeta = (selector, attrName, value) => {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    // Selector format: meta[<attr>="<value>"] — extract both
    const m = selector.match(/meta\[(\w+)="([^"]+)"\]/);
    if (m) el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attrName, value);
};

const upsertCanonical = (href) => {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

export default function SEOHead({
  title,
  description = DEFAULT_DESC,
  image = DEFAULT_IMG,
  url,
  type = 'website',
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Our Quality. Definitely Our Distinction.`;
    const canonical = url || (typeof window !== 'undefined' ? `${SITE_URL}${window.location.pathname}` : SITE_URL);

    document.title = fullTitle;
    upsertMeta('meta[name="description"]', 'content', description);

    upsertCanonical(canonical);

    // Open Graph
    upsertMeta('meta[property="og:title"]', 'content', fullTitle);
    upsertMeta('meta[property="og:description"]', 'content', description);
    upsertMeta('meta[property="og:url"]', 'content', canonical);
    upsertMeta('meta[property="og:image"]', 'content', image);
    upsertMeta('meta[property="og:type"]', 'content', type);
    upsertMeta('meta[property="og:site_name"]', 'content', SITE_NAME);

    // Twitter
    upsertMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'content', fullTitle);
    upsertMeta('meta[name="twitter:description"]', 'content', description);
    upsertMeta('meta[name="twitter:image"]', 'content', image);

    // Robots — opt out of indexing for private routes (admin, account, checkout)
    upsertMeta('meta[name="robots"]', 'content', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');
  }, [title, description, image, url, type, noindex]);

  return null;
}

export { SITE_URL, SITE_NAME, DEFAULT_DESC, DEFAULT_IMG };
